import React, { createContext, useCallback, useContext, useState } from "react";
import { notification } from "antd";
import { useSelector } from "react-redux";
import useBankConciliationContext from "../../../../../../../../hooks/useBankConciliationContext";
import {
  storeBankStatementFarm,
  uploadBankStatementFarm,
} from "../../../../../../../../services/bankStatementService";
import StepReviewImport from "./steps/stepReviewImport";
import StepUploadFile from "./steps/stepUploadFile";
import useStatementContext from "../../../../../../../../hooks/useStatementContext";
import { useConciliationContext } from "../../../../../../../../hooks/useConciliationContext";

const IMPORT_STATEMENT_FORM_CONTEXT_STATE = {
  isLoading: false,
  formData: null,
  steps: [],
  fileList: [],
  currentStep: 0,
  selectedRowsKeys: [],
  setSelectedRowKeys: () => {},
  selectFile: () => {},
  next: () => {},
  prev: () => {},
};

const ImportStatementFormContext = createContext(
  IMPORT_STATEMENT_FORM_CONTEXT_STATE
);

export const ImportStatementFormProvider = ({ children }) => {
  const steps = [
    {
      title: "step1",
      content: <StepUploadFile />,
    },
    {
      title: "step2",
      content: <StepReviewImport />,
    },
    {
      title: "step3",
      content: <StepReviewImport />,
    },
  ];
  const [selectedRowsKeys, setSelectedRowKeys] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  const { handleTabClick } = useBankConciliationContext();
  const { handleCloseStatementImportForm } = useStatementContext();
  const { fetchData: fetchConciliationTableData } = useConciliationContext();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  function cleanData() {
    setFormData(null);
    setFileList([]);
  }

  async function next() {
    try {
      await handleNextActionInStep();
      if (currentStep !== steps.length - 1) setCurrentStep((prev) => prev + 1);
    } catch (error) {}
  }

  function prev() {
    setCurrentStep((prev) => prev - 1);
  }

  function selectFile(file) {
    setFileList(file);
  }

  const handleUploadFile = useCallback(async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", fileList[0]);

      const {
        data: { results },
      } = await uploadBankStatementFarm({
        groupId,
        farmId,
        body: formData,
      });

      setFormData(results);

      notification.success({
        message: translation.financial.bankConciliation.statement.fileLoaded,
      });
    } catch (error) {
      let errorMessage = translation.financial.bankConciliation.statement.fileError;
      if (Object.keys(error).includes("response")) {
        const { response } = error;
        if (response && response.data) {
          const { code: errorCode } = response.data;
          switch (errorCode) {
            case 5039:
              errorMessage = translation.financial.bankConciliation.statement.fileErrorBankNotRegistered;
              break;
            case 5040:
              errorMessage = translation.financial.bankConciliation.statement.fileErrorAccountNotRegistered;
              break;
            case 5041:
              errorMessage = translation.financial.bankConciliation.statement.fileErrorAlreadyImported;
              break;
            default:
              break;
          }
        }
      }
      notification.error({
        message: errorMessage,
      });
      throw new Error("Error uploading file");
    } finally {
      setIsLoading(false);
    }
  }, [farmId, fileList, groupId]);

  const handleSubmitFormStatement = useCallback(async () => {
    setIsLoading(true);
    try {
      const body = {
        ...formData,
        transactions: formData.transactions.map((item) => {
          const shouldConciliate = selectedRowsKeys.includes(item.id);
          return {
            ...item,
            shouldConciliate,
          };
        }),
      };
      await storeBankStatementFarm({
        groupId,
        farmId,
        body,
      });
      notification.success({
        message: translation.financial.bankConciliation.statement.statementSaved,
      });
      setIsLoading(false);
      cleanData();
      handleCloseStatementImportForm();
      handleTabClick("1");
      fetchConciliationTableData(0, 10, {}, "");
    } catch (error) {
      notification.error({
        message: translation.financial.bankConciliation.statement.statementSaveError,
      });
      setIsLoading(false);
    }
  }, [
    farmId,
    fetchConciliationTableData,
    formData,
    groupId,
    handleCloseStatementImportForm,
    handleTabClick,
    selectedRowsKeys,
  ]);

  const handleValidateNextActionInStep = useCallback(() => {
    const step = steps[currentStep];
    switch (step.title) {
      case "step1":
        if (fileList.length === 0) {
          notification.error({
            message: translation.financial.bankConciliation.statement.selectFile,
          });
          return false;
        }
        break;
      case "step2":
        if (selectedRowsKeys.length === 0) {
          notification.error({
            message: translation.financial.bankConciliation.statement.selectRecord,
          });
          return false;
        }
        break;
      case "step3":
        break;
      default:
        break;
    }
    return true;
  }, [currentStep, fileList.length, selectedRowsKeys.length, steps]);

  const handleNextActionInStep = useCallback(async () => {
    const step = steps[currentStep];
    if (!handleValidateNextActionInStep()) {
      throw new Error("Invalid step");
    }
    switch (step.title) {
      case "step1":
        await handleUploadFile();
        break;
      case "step2":
        break;
      case "step3":
        await handleSubmitFormStatement();
        break;
      default:
        break;
    }
  }, [
    currentStep,
    handleSubmitFormStatement,
    handleUploadFile,
    handleValidateNextActionInStep,
    steps,
  ]);

  const values = {
    formData,
    isLoading,
    steps,
    fileList,
    currentStep,
    selectedRowsKeys,
    setSelectedRowKeys,
    next,
    prev,
    selectFile,
  };

  return (
    <ImportStatementFormContext.Provider value={values}>
      {children}
    </ImportStatementFormContext.Provider>
  );
};

export const useImportStatementFormContext = () => {
  const context = useContext(ImportStatementFormContext);

  if (!context) {
    throw new Error(
      "useImportStatementFormContext must be used within an ImportStatementFormProvider"
    );
  }

  return context;
};
